import React, { useEffect, useId, useMemo, useState } from 'react';
import { FiMaximize2, FiMinimize2, FiZoomIn } from 'react-icons/fi';
import TimeSeriesPlot from './TimeSeriesPlot';
import ChartNavigator from './ChartNavigator';
import type { ChartSpec } from './types';
import { validateChartSpec } from './validateChartSpec';
import { chartTimeMilliseconds } from './timeRange';
import { NAVIGATOR_MIN_POINTS } from './displayDensity';
import { chartLayers } from './layers';
import './UnifiedChart.css';

const MaximizeIcon = FiMaximize2 as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const MinimizeIcon = FiMinimize2 as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const ZoomIcon = FiZoomIn as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const DEFAULT_RANGE_PRESETS = ['1D', '1W', '1M', '3M', '6M', '1Y', '2Y', '5Y', 'ALL'] as const;

type ChartLabelMode = 'auto' | 'hover' | 'always' | 'hidden';

function chartLabelMode(value: string): ChartLabelMode {
  switch (value) {
    case 'hover':
    case 'always':
    case 'hidden':
      return value;
    default:
      return 'auto';
  }
}

type UnifiedChartProps = {
  spec: ChartSpec;
  sourceLabel?: string;
  observedAt?: string;
  height?: number;
  symbolSelector?: React.ReactNode;
};

export default function UnifiedChart({
  spec,
  sourceLabel,
  observedAt,
  height = 520,
  symbolSelector,
}: UnifiedChartProps): React.ReactElement {
  const chartSyncId = useId();
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const [hiddenLayers, setHiddenLayers] = useState<Set<string>>(new Set());
  const [hiddenMarkerSides, setHiddenMarkerSides] = useState<Set<'buy' | 'sell'>>(new Set());
  const [layerSearch, setLayerSearch] = useState('');
  const [labelMode, setLabelMode] = useState<ChartLabelMode>('auto');
  const [visibleRange, setVisibleRange] = useState<[number, number]>([0, 0]);
  const [activeRangePreset, setActiveRangePreset] = useState<string | null>('ALL');
  const [scaleMode, setScaleMode] = useState<'linear' | 'log' | 'percentage' | 'indexed'>('linear');
  const errors = useMemo(() => (spec ? validateChartSpec(spec) : []), [spec]);
  const layers = useMemo(() => chartLayers(spec), [spec]);
  const renderedSpec = useMemo(() => {
    if (!spec || (scaleMode !== 'percentage' && scaleMode !== 'indexed')) return spec;
    const baselines = new Map<string, number>();
    spec.series.forEach((series) => {
      if (series.role === 'volume' || series.placement === 'price-volume-overlay') return;
      if (!series.dataKey) return;
      const first = spec.data.find((row) => {
        const candidate = row[series.dataKey!];
        return (
          candidate !== null &&
          candidate !== undefined &&
          candidate !== '' &&
          Number.isFinite(Number(candidate))
        );
      })?.[series.dataKey];
      const value = Number(first);
      if (Number.isFinite(value) && value !== 0) baselines.set(series.dataKey, value);
    });
    return {
      ...spec,
      data: spec.data.map((row) => {
        const next = { ...row };
        baselines.forEach((baseline, key) => {
          const value = Number(row[key]);
          if (
            row[key] === null ||
            row[key] === undefined ||
            row[key] === '' ||
            !Number.isFinite(value)
          )
            return;
          next[key] =
            scaleMode === 'indexed' ? (value / baseline) * 100 : (value / baseline - 1) * 100;
        });
        return next;
      }),
      axes: spec.axes.map((axis) => {
        const isVolumeOverlayAxis = spec.series.some(
          (series) => series.axisId === axis.id && series.placement === 'price-volume-overlay',
        );
        return isVolumeOverlayAxis
          ? axis
          : {
              ...axis,
              format: scaleMode === 'percentage' ? ('percent' as const) : ('number' as const),
            };
      }),
    };
  }, [scaleMode, spec]);

  useEffect(() => {
    const lastIndex = Math.max(0, (spec?.data.length || 1) - 1);
    setActiveIndex(lastIndex);
    setVisibleRange([0, lastIndex]);
    setActiveRangePreset('ALL');
    setHiddenMarkerSides(new Set());
    setHiddenLayers(
      new Set(layers.filter((layer) => layer.defaultVisible === false).map((layer) => layer.id)),
    );
    setLayerSearch('');
    setLabelMode('auto');
  }, [spec, layers]);

  if (!spec) {
    return (
      <section
        className="unified-chart unified-chart--invalid"
        data-testid="unified-chart"
        role="alert"
      >
        <strong>Unable to display this chart</strong>
        <p>The chart payload has no explicit series data.</p>
      </section>
    );
  }

  if (errors.length > 0) {
    return (
      <section
        className="unified-chart unified-chart--invalid"
        data-testid="unified-chart"
        role="alert"
      >
        <strong>Unable to display this chart</strong>
        <p>The chart payload is missing required structural metadata.</p>
        <ul>
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </section>
    );
  }

  const renderedPanes = spec.panes.filter((pane) =>
    spec.series.some((series) => series.paneId === pane.id),
  );
  const totalWeight = renderedPanes.reduce((sum, pane) => sum + pane.weight, 0);
  const plotHeight = expanded ? Math.max(window.innerHeight - 330, 520) : height;
  const controls = spec.controls || {};
  const markerLegend = [
    ...(spec.markers || [])
      .reduce((groups, marker) => {
        if (!marker.compact) return groups;
        const key = marker.side || marker.label;
        const label =
          marker.side === 'buy' ? 'Buy' : marker.side === 'sell' ? 'Sell' : marker.label;
        const existing = groups.get(key);
        groups.set(
          key,
          existing
            ? { ...existing, count: existing.count + 1 }
            : {
                key,
                label,
                side: marker.side,
                color: marker.color,
                shape: marker.shape,
                count: 1,
              },
        );
        return groups;
      }, new Map<string, { key: string; label: string; side?: 'buy' | 'sell'; color: string; shape: string; count: number }>())
      .values(),
  ];
  const showVisibilityControls = controls.allowSeriesToggle !== false && layers.length > 0;
  const filteredLayers = layers.filter((layer) =>
    `${layer.label} ${layer.description || ''}`
      .toLowerCase()
      .includes(layerSearch.trim().toLowerCase()),
  );
  const referenceLabelCount = (spec.referenceLines || []).filter((line) => line.label).length;
  const effectiveLabelMode =
    labelMode === 'auto' ? (referenceLabelCount > 6 ? 'hover' : 'always') : labelMode;
  const resetLayers = () =>
    setHiddenLayers(
      new Set(layers.filter((layer) => layer.defaultVisible === false).map((layer) => layer.id)),
    );

  const chooseRange = (preset: string) => {
    const last = spec.data.length - 1;
    if (preset === 'ALL' || spec.xAxis.type !== 'time') {
      setVisibleRange([0, last]);
      setActiveRangePreset(preset);
      return;
    }
    const lastTime = chartTimeMilliseconds(spec.data[last][spec.xAxis.key]);
    if (lastTime === null) return;
    const dayCounts: Record<string, number> = {
      '1D': 1,
      '1W': 7,
      '5D': 5,
      '1M': 31,
      '3M': 93,
      '6M': 186,
      '1Y': 366,
      '2Y': 365 * 2,
      '3Y': 365 * 3,
      '5Y': 365 * 5,
    };
    const dayCount = dayCounts[preset] ?? 366;
    const threshold =
      preset === 'YTD'
        ? Date.UTC(new Date(lastTime).getUTCFullYear(), 0, 1)
        : lastTime - dayCount * 86_400_000;
    const start = Math.max(
      0,
      spec.data.findIndex((row) => {
        const value = chartTimeMilliseconds(row[spec.xAxis.key]);
        return value !== null && value >= threshold;
      }),
    );
    setVisibleRange([start, last]);
    setActiveRangePreset(preset);
  };

  const zoom = (direction: 'in' | 'out') => {
    const [start, end] = visibleRange;
    const span = end - start + 1;
    const delta = Math.max(1, Math.floor(span * 0.2));
    if (direction === 'in' && span > 4) {
      setVisibleRange([start + Math.floor(delta / 2), end - Math.ceil(delta / 2)]);
      setActiveRangePreset(null);
    }
    if (direction === 'out') {
      setVisibleRange([Math.max(0, start - delta), Math.min(spec.data.length - 1, end + delta)]);
      setActiveRangePreset(null);
    }
  };

  const navigatorValues = spec.data.map((row) => {
    const price = spec.series.find((item) => item.role === 'price') || spec.series[0];
    if (price?.mark === 'candlestick' && price.fieldMapping) {
      const value = Number(row[price.fieldMapping.close]);
      return Number.isFinite(value) ? value : null;
    }
    if (!price?.dataKey) return null;
    const value = Number(row[price.dataKey]);
    return Number.isFinite(value) ? value : null;
  });
  const showNavigator =
    spec.kind === 'timeseries' &&
    controls.allowZoom !== false &&
    spec.data.length >= NAVIGATOR_MIN_POINTS;
  const rangePresets = controls.rangePresets?.length
    ? controls.rangePresets
    : DEFAULT_RANGE_PRESETS;

  const pan = (direction: 'left' | 'right') => {
    const [start, end] = visibleRange;
    const shift = Math.max(1, Math.floor((end - start + 1) * 0.2));
    if (direction === 'left') {
      const nextStart = Math.max(0, start - shift);
      setVisibleRange([nextStart, nextStart + (end - start)]);
    } else {
      const nextEnd = Math.min(spec.data.length - 1, end + shift);
      setVisibleRange([nextEnd - (end - start), nextEnd]);
    }
    setActiveRangePreset(null);
  };
  const canPanLeft = visibleRange[0] > 0;
  const canPanRight = visibleRange[1] < spec.data.length - 1;
  const toggleExpanded = () => setExpanded((value) => !value);

  return (
    <section
      className={`unified-chart ${expanded ? 'unified-chart--expanded' : ''} unified-chart--labels-${effectiveLabelMode}`}
      data-testid="unified-chart"
      data-chart-kind={spec.kind}
      data-chart-profile={spec.profile || 'analytical'}
      data-chart-row-count={spec.data.length}
      aria-label={spec.title}
    >
      <header className="unified-chart__header">
        <div className="unified-chart__header-copy">
          <div className="unified-chart__title-row" data-testid="chart-title-row">
            <h4>{spec.title}</h4>
            {symbolSelector ? (
              <div className="unified-chart__symbol-selector" data-testid="chart-symbol-selector">
                {symbolSelector}
              </div>
            ) : null}
          </div>
          {spec.profile !== 'financial' && spec.subtitle && (
            <p className="unified-chart__subtitle">{spec.subtitle}</p>
          )}
          {spec.explanation && (
            <details className="unified-chart__details" data-testid="chart-details">
              <summary>About this chart</summary>
              <p className="unified-chart__explanation">{spec.explanation}</p>
            </details>
          )}
        </div>
        <div className="unified-chart__header-actions" data-testid="chart-header-actions">
          <div className="unified-chart__controls" aria-label="Chart controls">
            {controls.allowZoom !== false && (
              <span
                className="unified-chart__control-group unified-chart__control-group--zoom"
                aria-label="Zoom controls"
              >
                <strong className="unified-chart__control-label">
                  <ZoomIcon data-testid="chart-zoom-icon" aria-hidden />
                  <span>Zoom</span>
                </strong>
                <button
                  className="unified-chart__zoom-step"
                  type="button"
                  onClick={() => zoom('in')}
                  aria-label="Zoom in"
                >
                  +
                </button>
                <button
                  className="unified-chart__zoom-step"
                  type="button"
                  onClick={() => zoom('out')}
                  aria-label="Zoom out"
                >
                  −
                </button>
                <button
                  className="unified-chart__zoom-reset"
                  type="button"
                  onClick={() => {
                    setVisibleRange([0, Math.max(0, spec.data.length - 1)]);
                    setActiveRangePreset('ALL');
                  }}
                  aria-label="Reset zoom"
                >
                  Reset
                </button>
              </span>
            )}
            {controls.allowPan !== false && (
              <span className="unified-chart__control-group" aria-label="Pan controls">
                <button
                  type="button"
                  onClick={() => pan('left')}
                  aria-label="Pan left"
                  disabled={!canPanLeft}
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => pan('right')}
                  aria-label="Pan right"
                  disabled={!canPanRight}
                >
                  →
                </button>
              </span>
            )}
            {(controls.scaleModes || []).map((mode) => (
              <button
                key={mode}
                type="button"
                className={scaleMode === mode ? 'is-active' : ''}
                onClick={() => setScaleMode(mode)}
              >
                {mode}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="unified-chart__expand"
            onClick={toggleExpanded}
            aria-label={expanded ? 'Exit full screen chart' : 'Expand chart'}
            aria-expanded={expanded}
          >
            {expanded ? <MinimizeIcon aria-hidden /> : <MaximizeIcon aria-hidden />}
            <span>{expanded ? 'Close expanded view' : 'Expand chart'}</span>
          </button>
        </div>
      </header>

      <div className="unified-chart__toolbar" data-testid="chart-toolbar">
        {showVisibilityControls && (
          <div
            className="unified-chart__layer-manager"
            aria-label="Chart series and markers visibility"
          >
            <div className="unified-chart__layer-actions">
              {controls.allowLayerSearch !== false && layers.length > 6 && (
                <label>
                  <span className="sr-only">Search chart layers</span>
                  <input
                    type="search"
                    value={layerSearch}
                    onChange={(event) => setLayerSearch(event.target.value)}
                    placeholder="Search layers"
                    aria-label="Search chart layers"
                  />
                </label>
              )}
              <button
                type="button"
                onClick={() => setHiddenLayers(new Set())}
                aria-label="Show all chart layers"
              >
                Show all
              </button>
              <button
                type="button"
                onClick={() => setHiddenLayers(new Set(layers.map((layer) => layer.id)))}
                aria-label="Hide all chart layers"
              >
                Hide all
              </button>
              <button type="button" onClick={resetLayers} aria-label="Reset chart layers">
                Reset
              </button>
              {controls.allowLabelControls !== false && (
                <label className="unified-chart__label-mode">
                  <span>Labels</span>
                  <select
                    value={labelMode}
                    onChange={(event) => setLabelMode(chartLabelMode(event.target.value))}
                    aria-label="Chart label visibility"
                  >
                    <option value="auto">Auto</option>
                    <option value="hover">On hover</option>
                    <option value="always">Always</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </label>
              )}
            </div>
            <div className="unified-chart__layer-list">
              {filteredLayers.map((layer) => {
                const hidden = hiddenLayers.has(layer.id);
                const isLegacyOverlayGroup = (spec.overlayGroups || []).some(
                  (group) => group.id === layer.id,
                );
                return (
                  <button
                    key={layer.id}
                    type="button"
                    className={hidden ? 'is-hidden' : ''}
                    aria-label={
                      isLegacyOverlayGroup
                        ? `${hidden ? 'Show' : 'Hide'} ${layer.label}`
                        : layer.label
                    }
                    aria-pressed={!hidden}
                    title={layer.description || layer.label}
                    onClick={() =>
                      setHiddenLayers((current) => {
                        const next = new Set(current);
                        if (next.has(layer.id)) next.delete(layer.id);
                        else next.add(layer.id);
                        return next;
                      })
                    }
                  >
                    <i
                      className="unified-chart__series-swatch"
                      style={{
                        backgroundColor: layer.style?.color || layer.style?.fill,
                        opacity: layer.style?.opacity,
                      }}
                      aria-hidden
                    />
                    <span>{layer.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="unified-chart__marker-legend" aria-label="Trade marker visibility">
              {markerLegend.map((item) => {
                const hidden = item.side ? hiddenMarkerSides.has(item.side) : false;
                const contents = (
                  <>
                    <i
                      className={`unified-chart__marker-symbol unified-chart__marker-symbol--${item.shape}`}
                      style={{ color: item.color }}
                      aria-hidden
                    >
                      {item.shape === 'arrowUp'
                        ? '↑'
                        : item.shape === 'arrowDown'
                          ? '↓'
                          : item.shape === 'square'
                            ? '■'
                            : '●'}
                    </i>
                    {item.label}
                    <strong>{item.count.toLocaleString()}</strong>
                  </>
                );
                return item.side ? (
                  <button
                    key={item.key}
                    type="button"
                    className={hidden ? 'is-hidden' : ''}
                    data-marker-legend-item={item.label}
                    aria-label={`${hidden ? 'Show' : 'Hide'} ${item.label} trades (${item.count})`}
                    aria-pressed={!hidden}
                    onClick={() =>
                      setHiddenMarkerSides((current) => {
                        const next = new Set(current);
                        if (next.has(item.side!)) next.delete(item.side!);
                        else next.add(item.side!);
                        return next;
                      })
                    }
                  >
                    {contents}
                  </button>
                ) : (
                  <span key={item.key} data-marker-legend-item={item.label}>
                    {contents}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="unified-chart__plot" style={{ height: plotHeight }}>
        {renderedPanes.map((pane, index) => (
          <TimeSeriesPlot
            key={pane.id}
            syncId={chartSyncId}
            spec={renderedSpec || spec}
            pane={pane}
            height={(plotHeight * pane.weight) / totalWeight}
            showXAxis={index === renderedPanes.length - 1}
            visibleRange={visibleRange}
            activeIndex={activeIndex}
            hiddenSeries={hiddenSeries}
            hiddenLayers={hiddenLayers}
            hiddenMarkerSides={hiddenMarkerSides}
            scaleMode={scaleMode}
            onHover={setActiveIndex}
          />
        ))}
      </div>

      {showNavigator && (
        <ChartNavigator
          pointCount={spec.data.length}
          visibleRange={visibleRange}
          values={navigatorValues}
          presets={rangePresets}
          activePreset={activeRangePreset}
          onChange={(range) => {
            setVisibleRange(range);
            setActiveRangePreset(null);
          }}
          onSelectPreset={chooseRange}
        />
      )}

      {(sourceLabel || observedAt || spec.notes) && (
        <footer className="unified-chart__footer">
          {sourceLabel && <span>{sourceLabel}</span>}
          {observedAt && <span>{new Date(observedAt).toLocaleString()}</span>}
          {spec.notes && <span>{spec.notes}</span>}
        </footer>
      )}
    </section>
  );
}
